import clsx from "clsx";
import { LucideX } from "lucide-react";
import type { ReactNode } from "react";
import { Button as AriaButton, Modal as AriaModal, Heading, ModalOverlay } from "react-aria-components";
import { Button } from "../button/button";

export interface ModalButtonProps {
	text?: string;
	iconLeft?: ReactNode;
	iconRight?: ReactNode;
	onClick: () => void;
	disabled?: boolean;
	loading?: boolean;
	className?: string;
}

export interface ModalProps {
	title?: string;
	onClose: () => void;
	closable?: boolean;
	show: boolean;
	children: ReactNode;
	size?: "sm" | "md" | "lg" | "xl" | "screen" | "fit";
	secondaryButton?: ModalButtonProps;
	primaryButton?: ModalButtonProps;
	dangerButton?: ModalButtonProps;
	headerBorder?: boolean;
	UNSTABLE_portalContainer?: Element;
	equalActionButtons?: boolean;
}

const sizes = {
	sm: "max-w-[480px]",
	md: "max-w-[728px]",
	lg: "max-w-[976px]",
	xl: "max-w-[1200px]",
	screen: "sm:w-full xl:mx-4 2xl:mx-16",
	fit: "w-fit max-w-fit",
};

const fallbackSize = "w-fit max-w-[calc(100vw-40px)]";

export function CommonModal({
	title,
	onClose,
	show = false,
	closable = true,
	children,
	size,
	secondaryButton,
	primaryButton,
	dangerButton,
	headerBorder = true,
	equalActionButtons = false,
	UNSTABLE_portalContainer,
}: ModalProps) {
	const actionCount = [secondaryButton, primaryButton, dangerButton].filter(Boolean).length;
	const shouldSplitActions = equalActionButtons && actionCount === 2;

	return (
		<ModalOverlay
			// Portaled react-select menus render on document.body; isDismissable must be false
			// or option clicks count as outside interaction. Backdrop dismiss is handled below
			// (same approach as ui-kit/src/Modal/Modal.tsx).
			isDismissable={false}
			isOpen={show}
			UNSTABLE_portalContainer={UNSTABLE_portalContainer}
			onOpenChange={(isOpen) => {
				if (!isOpen) {
					closable && onClose?.();
				}
			}}
			className={`fixed inset-0 ${show ? "block" : "hidden"}`}
			style={{ zIndex: 10000 }}
		>
			{closable ? (
				<div
					role="presentation"
					aria-hidden
					className="absolute inset-0 bg-[#343A60CC]"
					tabIndex={-1}
					onPointerDown={(e) => {
						if (e.target !== e.currentTarget) {
							return;
						}
						e.preventDefault();
						onClose();
					}}
				/>
			) : (
				<div role="presentation" aria-hidden className="absolute inset-0 bg-[#343A60CC]" />
			)}
			<div className="pointer-events-none absolute inset-0 flex items-center justify-center p-5">
				<AriaModal
					aria-label={title ?? ""}
					className={`${size ? `${sizes[size]} w-full` : fallbackSize} pointer-events-auto relative text-Text-primary shadow-md bg-white rounded-xl max-h-[calc(100vh-40px)] flex flex-col transition-[transform,opacity] duration-200 ease-out data-[entering]:opacity-0 data-[entering]:scale-[0.7] data-[exiting]:scale-[0.7] data-[exiting]:opacity-0`}
				>
				<div className={`flex items-center justify-between py-sm pl-lg pr-sm ${headerBorder ? "border-b border-Border-primary" : ""}`}>
					{title && <Heading className="text-lg font-semibold leading-none">{title}</Heading>}
					{closable && (
						<AriaButton
							onPress={onClose}
							className="ml-auto cursor-pointer rounded-md px-xs py-[6px] text-text-secondary transition-colors hover:bg-neutral-100 hover:text-gray-800"
						>
							<LucideX size={22} />
						</AriaButton>
					)}
				</div>
				<div className="p-lg flex-1 min-h-0 overflow-y-auto">{children}</div>
				{(secondaryButton || primaryButton || dangerButton) && (
					<div className="border-t border-Border-primary bg-Background-secondary/30 p-sm">
						<div className={shouldSplitActions ? "grid grid-cols-2 gap-sm" : "flex justify-end gap-sm"}>
							{secondaryButton &&
								(() => {
									const {
										className: secCls,
										iconLeft: secIcon,
										iconRight: secRight,
										text: secText,
										onClick: secOnClick,
										disabled: secDisabled,
									} = secondaryButton;
									return (
										<Button
											variant="secondary"
											type="button"
											text={secText}
											onClick={secOnClick}
											disabled={secDisabled}
											iconRight={secRight}
											className={clsx(
												shouldSplitActions ? "w-full" : "!w-auto min-w-0 shrink-0",
												"transition-colors hover:!bg-neutral-100 focus-visible:!bg-neutral-100",
												secCls
											)}
											iconLeft={secIcon}
										/>
									);
								})()}
							{primaryButton &&
								(() => {
									const {
										className: priCls,
										iconLeft: priIcon,
										iconRight: priRight,
										text: priText,
										onClick: priOnClick,
										disabled: priDisabled,
										loading: priLoading,
									} = primaryButton;
									return (
										<Button
											variant="primary"
											type="button"
											text={priText}
											onClick={priOnClick}
											disabled={priDisabled}
											loading={priLoading}
											iconRight={priRight}
											className={clsx(
												shouldSplitActions ? "w-full" : "!w-auto min-w-0 shrink-0",
												"transition-colors hover:!brightness-[1.06] hover:shadow-sm",
												priCls
											)}
											iconLeft={priIcon}
										/>
									);
								})()}
							{dangerButton &&
								(() => {
									const {
										className: dCls,
										iconLeft: dIcon,
										iconRight: dRight,
										text: dText,
										onClick: dOnClick,
										disabled: dDisabled,
									} = dangerButton;
									return (
										<Button
											variant="danger"
											type="button"
											text={dText}
											onClick={dOnClick}
											disabled={dDisabled}
											iconRight={dRight}
											className={clsx(
												shouldSplitActions
													? "w-full text-white bg-red-500"
													: "!w-auto min-w-0 shrink-0 text-white bg-red-500",
												"transition-colors hover:!bg-red-600 focus-visible:!bg-red-600",
												dCls
											)}
											iconLeft={dIcon}
										/>
									);
								})()}
						</div>
					</div>
				)}
			</AriaModal>
			</div>
		</ModalOverlay>
	);
}
